(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${ROOT}cart`;
  const CART_JSON_URL = `${ROOT}cart.js`;
  const CART_UPDATE_URL = `${ROOT}cart/update.js`;
  const CHECKOUT_URL = `${ROOT}checkout`;

  const COMPLETE_KEY = 'FCSS Information Complete';
  const REQUIRED_EMAIL_DOMAIN = 'fcoe.org';
  const LEGACY_WORKSITE_KEY = 'FCSS Worksite';
  const LEGACY_EMPLOYEE_ID_KEY = 'FCSS Employee ID';

  const REQUIRED_ATTRIBUTE_KEYS = [
    'FCSS First name',
    'FCSS Last name',
    'FCSS Work email',
    'FCSS Phone number',
    'FCSS Department or division',
  ];

  const normalize = (value) => String(value ?? '').trim();

  const cartAttributesAreComplete = (attributes = {}) => {
    if (normalize(attributes[COMPLETE_KEY]).toLowerCase() !== 'yes') return false;

    const requiredValuesPresent = REQUIRED_ATTRIBUTE_KEYS.every(
      (key) => normalize(attributes[key]) !== ''
    );

    if (!requiredValuesPresent) return false;

    const workEmail = normalize(attributes['FCSS Work email']).toLowerCase();
    const atIndex = workEmail.lastIndexOf('@');

    if (atIndex <= 0 || atIndex >= workEmail.length - 1) return false;

    return workEmail.slice(atIndex + 1) === REQUIRED_EMAIL_DOMAIN;
  };

  const fetchCart = async () => {
    const response = await fetch(CART_JSON_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status})`);
    }

    return response.json();
  };

  const updateCart = async (payload) => {
    const response = await fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let message = `Unable to save cart information (${response.status})`;

      try {
        const error = await response.json();
        message = error?.description || error?.message || message;
      } catch (_) {}

      throw new Error(message);
    }

    return response.json();
  };

  class FCSSCheckoutInformation extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-fcss-form]');
      this.note = this.querySelector('[data-fcss-note]');
      this.status = this.querySelector('[data-fcss-status]');
      this.fields = Array.from(this.querySelectorAll('[data-fcss-field]'));

      this.approvedDomains = normalize(this.dataset.approvedDomains)
        .split(',')
        .map((domain) => domain.trim().toLowerCase().replace(/^@/, ''))
        .filter(Boolean);

      this.allowSubdomains = this.dataset.allowSubdomains === 'true';
      this.phoneMinDigits = Number.parseInt(this.dataset.phoneMinDigits || '10', 10) || 10;
      this.autoSaveDelay = Number.parseInt(this.dataset.autoSaveDelay || '600', 10) || 600;

      this.requiredMessage = this.dataset.requiredMessage || 'This field is required.';
      this.emailMessage = this.dataset.emailMessage || 'Please enter your FCSS work email ending in @fcoe.org.';
      this.phoneMessage = this.dataset.phoneMessage || 'Please enter a valid phone number.';
      this.incompleteMessage =
        this.dataset.incompleteMessage || 'Please complete the required information before checkout.';

      this.saveTimer = null;
      this.activeSave = null;
      this.lastSavedSignature = '';

      this.fields.forEach((field) => {
        field.addEventListener('input', () => {
          this.validateField(field, false);
          this.queueSave();
        });

        field.addEventListener('change', () => {
          this.validateField(field, false);
          this.queueSave();
        });

        field.addEventListener('blur', () => {
          this.validateField(field, true);
          this.queueSave(0);
        });
      });

      this.note?.addEventListener('input', () => this.queueSave());
      this.note?.addEventListener('blur', () => this.queueSave(0));

      this.updateCheckoutState();

      if (window.location.hash === '#fcss-checkout-information') {
        window.setTimeout(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.firstInvalidOrFirstField()?.focus({ preventScroll: true });
        }, 250);
      }
    }

    firstInvalidOrFirstField() {
      return this.querySelector('.fcss-field.is-invalid [data-fcss-field]') || this.fields[0] || null;
    }

    valueOf(field) {
      return normalize(field?.value);
    }

    emailIsAllowed(email) {
      const normalizedEmail = normalize(email).toLowerCase();
      const atIndex = normalizedEmail.lastIndexOf('@');

      if (atIndex <= 0 || atIndex >= normalizedEmail.length - 1) return false;

      const localPart = normalizedEmail.slice(0, atIndex);
      const domain = normalizedEmail.slice(atIndex + 1);

      return Boolean(localPart) && domain === REQUIRED_EMAIL_DOMAIN;
    }

    validateField(field, showError = true) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) {
        return true;
      }

      const fieldName = field.dataset.fcssField || '';
      const value = this.valueOf(field);
      let message = '';

      if (field.required && !value) {
        message = this.requiredMessage;
      } else if (fieldName === 'work_email' && value && !this.emailIsAllowed(value)) {
        message = this.emailMessage;
      } else if (fieldName === 'phone' && value) {
        const digits = value.replace(/\D/g, '').length;

        if (digits < this.phoneMinDigits) {
          message = this.phoneMessage;
        }
      }

      const wrapper = field.closest('.fcss-field');
      const errorElement = this.querySelector(`[data-error-for="${fieldName}"]`);

      if (message && showError) {
        wrapper?.classList.add('is-invalid');
        field.setAttribute('aria-invalid', 'true');

        if (errorElement) {
          errorElement.textContent = message;
          errorElement.hidden = false;
        }
      } else if (!message) {
        wrapper?.classList.remove('is-invalid');
        field.removeAttribute('aria-invalid');

        if (errorElement) {
          errorElement.textContent = '';
          errorElement.hidden = true;
        }
      }

      return !message;
    }

    validateAll({ focus = false } = {}) {
      let valid = true;
      let firstInvalid = null;

      this.fields.forEach((field) => {
        const fieldValid = this.validateField(field, true);

        if (!fieldValid) {
          valid = false;
          if (!firstInvalid) firstInvalid = field;
        }
      });

      if (!valid && focus && firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });

        window.setTimeout(() => {
          firstInvalid.focus({ preventScroll: true });
        }, 250);
      }

      this.updateCheckoutState(valid);
      return valid;
    }

    buildPayload() {
      const attributes = {};

      this.fields.forEach((field) => {
        const key = field.dataset.cartKey;
        if (!key) return;
        attributes[key] = this.valueOf(field);
      });

      // Worksite was removed. Clear any legacy value from older carts.
      attributes[LEGACY_WORKSITE_KEY] = '';
      attributes[LEGACY_EMPLOYEE_ID_KEY] = '';

      const valid = this.fields.every((field) => this.validateField(field, false));
      attributes[COMPLETE_KEY] = valid ? 'Yes' : 'No';

      return {
        attributes,
        note: normalize(this.note?.value),
      };
    }

    getPayloadSignature(payload) {
      return JSON.stringify(payload);
    }

    setStatus(message = '', state = '') {
      if (!this.status) return;

      this.status.textContent = message;

      if (state) {
        this.status.dataset.state = state;
      } else {
        delete this.status.dataset.state;
      }
    }

    queueSave(delay = this.autoSaveDelay) {
      window.clearTimeout(this.saveTimer);

      this.saveTimer = window.setTimeout(() => {
        this.save({ silent: true }).catch(() => {});
      }, Math.max(0, delay));
    }

    async save({ silent = false, force = false } = {}) {
      window.clearTimeout(this.saveTimer);

      const payload = this.buildPayload();
      const signature = this.getPayloadSignature(payload);

      if (!force && signature === this.lastSavedSignature) {
        return payload;
      }

      if (!silent) {
        this.setStatus('Saving your information…', 'saving');
      }

      const request = updateCart(payload);
      this.activeSave = request;

      try {
        await request;
        this.lastSavedSignature = signature;

        if (!silent) {
          this.setStatus('Information saved.', 'saved');

          window.setTimeout(() => {
            if (this.status?.dataset.state === 'saved') {
              this.setStatus();
            }
          }, 1800);
        }

        return payload;
      } catch (error) {
        console.error('[FCSS checkout information]', error);
        this.setStatus(error?.message || 'Unable to save your information.', 'error');
        throw error;
      } finally {
        if (this.activeSave === request) {
          this.activeSave = null;
        }
      }
    }

    async prepareCheckout() {
      if (!this.validateAll({ focus: true })) {
        this.setStatus(this.incompleteMessage, 'error');
        return false;
      }

      try {
        this.setStatus('Saving your information…', 'saving');
        await this.save({ silent: true, force: true });
        this.setStatus('Information saved. Continuing to checkout…', 'saved');
        return true;
      } catch (_) {
        return false;
      }
    }

    updateCheckoutState(valid = null) {
      const isValid =
        valid ??
        this.fields.every((field) => this.validateField(field, false));

      document.querySelectorAll(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      ).forEach((control) => {
        control.dataset.fcssInformationRequired = 'true';

        if (isValid) {
          delete control.dataset.fcssInformationIncomplete;
        } else {
          control.dataset.fcssInformationIncomplete = 'true';
        }
      });
    }
  }

  if (!customElements.get('fcss-checkout-information')) {
    customElements.define('fcss-checkout-information', FCSSCheckoutInformation);
  }

  const findCheckoutControl = (target) => {
    if (!(target instanceof Element)) return null;

    return target.closest(
      'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
    );
  };

  let navigatingToCheckout = false;

  const goToCheckout = () => {
    if (navigatingToCheckout) return;

    navigatingToCheckout = true;
    window.location.assign(CHECKOUT_URL);
  };

  const goToInformationForm = () => {
    const cartPath = CART_URL.replace(/\/+$/, '');
    const currentPath = window.location.pathname.replace(/\/+$/, '');
    const form = document.querySelector('fcss-checkout-information');

    if (currentPath === cartPath && form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });

      window.setTimeout(() => {
        form.firstInvalidOrFirstField?.()?.focus({ preventScroll: true });
      }, 250);

      return;
    }

    window.location.assign(`${CART_URL}#fcss-checkout-information`);
  };

  const guardCheckout = async (event, checkoutControl) => {
    if (!checkoutControl || navigatingToCheckout) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    const form = document.querySelector('fcss-checkout-information');

    if (form?.prepareCheckout) {
      const ready = await form.prepareCheckout();

      if (ready) {
        window.setTimeout(goToCheckout, 60);
      }

      return;
    }

    try {
      const cart = await fetchCart();

      if (cartAttributesAreComplete(cart.attributes || {})) {
        goToCheckout();
      } else {
        goToInformationForm();
      }
    } catch (error) {
      console.error('[FCSS checkout guard]', error);
      goToInformationForm();
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = findCheckoutControl(event.target);

      if (!checkoutControl) return;
      guardCheckout(event, checkoutControl);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;

      if (!(submitter instanceof HTMLElement)) return;

      const isCheckoutSubmit =
        submitter.getAttribute('name') === 'checkout' ||
        submitter.id === 'checkout';

      if (!isCheckoutSubmit) return;

      guardCheckout(event, submitter);
    },
    true
  );

  const flowStyles = document.createElement('style');
  flowStyles.id = 'fcss-checkout-information-guard-styles';
  flowStyles.textContent = `
    .additional-checkout-buttons {
      display: none !important;
    }

    [data-fcss-information-incomplete="true"] {
      opacity: .68;
    }
  `;

  document.head.appendChild(flowStyles);
})();